/**
 * Waits for all custom element descendants to be defined before invoking the callback.
 * Uses `customElements.whenDefined()` to deterministically wait for element registration,
 * with an optional timeout fallback.
 *
 * @param target - The parent element to check for undefined descendants.
 * @param callback - The callback to invoke when all descendants are defined.
 * @param options - Configuration options for the wait behavior.
 * @public
 *
 * @example
 * ```typescript
 * waitForConnectedDescendants(this, () => {
 *   // All custom element descendants are now defined
 *   this.processChildren();
 * });
 * ```
 */
export function waitForConnectedDescendants(target, callback, options) {
    var _a, _b;
    const shallow = (_a = options === null || options === void 0 ? void 0 : options.shallow) !== null && _a !== void 0 ? _a : false; // restricts the check to direct children only
    const timeout = (_b = options === null || options === void 0 ? void 0 : options.timeout) !== null && _b !== void 0 ? _b : 50; // timout after 50ms if definitions never arrive
    const query = `${shallow ? ":scope > " : ""}:not(:defined)`; // CSS pseudo class that matches unregistered custom elements
    const undefinedEls = target.querySelectorAll(query); // query for undefined elements
    // All descendants already defined — invoke immediately
    if (undefinedEls.length === 0) {
        // if every descendant is already defined fire the callback
        callback();
        return;
    }
    // Collect unique tag names that need defining
    const tagNames = new Set();
    for (const el of undefinedEls) {
        tagNames.add(el.localName);
    }
    const definedPromise = Promise.all([...tagNames].map((name) => customElements.whenDefined(name)) // returns a promise that resolves the moment the custom element is defined
    ); // wait for all registrants to be defined
    let settled = false;
    // timeout and promise will "race" to call the callback. settle function - guards against multiple calls of the callback
    const settle = () => {
        if (settled) {
            return;
        }
        settled = true;
        callback();
    };
    definedPromise.then(settle);
    // Optional timeout fallback — fires the callback if definitions take too long
    if (timeout > 0) {
        setTimeout(settle, timeout);
    }
}
//# sourceMappingURL=descendants.js.map